import { ajax } from "discourse/lib/ajax";
import DiscourseRoute from "discourse/routes/discourse";

// Metric metadata — drives unit formatting, winner direction, and display labels.
// Replaces the fragile regex approach with an explicit, maintainable map.
const METRIC_META = {
  price_per_gb:          { unit: "$", suffix: "/GB", better: "lower", label: "Price per GB" },
  cost_per_gb:           { unit: "$", suffix: "/GB", better: "lower", label: "Cost per GB" },
  real_cost_per_gb:      { unit: "$", suffix: "/GB", better: "lower", label: "Real Cost per GB" },
  avg_response_time_ms:  { unit: "",  suffix: "ms", better: "lower", label: "Avg Response Time" },
  min_response_time_ms:  { unit: "",  suffix: "ms", better: "lower", label: "Min Response Time" },
  max_response_time_ms:  { unit: "",  suffix: "ms", better: "lower", label: "Max Response Time" },
  p50_response_time_ms:  { unit: "",  suffix: "ms", better: "lower", label: "P50 Response Time" },
  p95_response_time_ms:  { unit: "",  suffix: "ms", better: "lower", label: "P95 Response Time" },
  success_rate:          { unit: "",  suffix: "%",  better: "higher", label: "Success Rate" },
  block_rate:            { unit: "",  suffix: "%",  better: "lower", label: "Block Rate" },
  verified_block_rate:   { unit: "",  suffix: "%",  better: "lower", label: "Verified Block Rate" },
  uptime_percentage:     { unit: "",  suffix: "%",  better: "higher", label: "Uptime" },
  ip_pool_size:          { unit: "",  suffix: "",   better: "higher", label: "IP Pool Size" },
  pool_size_million:     { unit: "",  suffix: "M",  better: "higher", label: "Pool Size" },
  requests_per_second:   { unit: "",  suffix: " rps", better: "higher", label: "Requests/sec" },
  overall_rating:        { unit: "★ ", suffix: "/5", better: "higher", label: "Overall Rating" },
  review_count:          { unit: "",  suffix: "",   better: "higher", label: "Review Count" },
};

function getMetricMeta(key) {
  return METRIC_META[key] || METRIC_META[key.toLowerCase().replace(/[\s-]/g, "_")];
}

function formatMetric(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  const meta = getMetricMeta(key);
  if (!meta) return String(value);
  const num = parseFloat(value);
  if (isNaN(num)) return `${meta.unit}${value}${meta.suffix}`;
  const formatted = num % 1 === 0 ? num.toString() : num.toFixed(2);
  return `${meta.unit}${formatted}${meta.suffix}`;
}

function metricLabel(key) {
  const meta = getMetricMeta(key);
  if (meta) return meta.label;
  // Fallback: titleize with GB/IP/ASN awareness
  return key
    .replace(/_/g, " ")
    .replace(/\bgb\b/gi, "GB")
    .replace(/\bip\b/gi, "IP")
    .replace(/\basn\b/gi, "ASN")
    .replace(/\bp(\d+)\b/gi, "P$1")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export default class ProxyviewsComparisonRoute extends DiscourseRoute {
  async model(params) {
    const slug = params.comparison_slug;
    const data = await ajax(`/compare/${slug}.json`);

    const c = data.comparison || {};
    const matrix = data.matrix || {};

    const slugA = c.provider_a_slug;
    const slugB = c.provider_b_slug;
    const rows = Object.entries(matrix)
      .filter(([key]) => key !== "faq" && key !== "providers")
      .map(([key, values]) => {
        const v = values || {};
        const a = v[slugA] ?? v[String(slugA)];
        const b = v[slugB] ?? v[String(slugB)];

        let winnerSide = null;
        const aNum = parseFloat(a);
        const bNum = parseFloat(b);
        if (!isNaN(aNum) && !isNaN(bNum) && aNum !== bNum) {
          const meta = getMetricMeta(key);
          const lowerIsBetter = meta ? meta.better === "lower" : /price|cost|response|latency|error|block/i.test(key);
          const aBetter = lowerIsBetter ? aNum < bNum : aNum > bNum;
          winnerSide = aBetter ? "a" : "b";
        }

        return {
          key,
          label: metricLabel(key),
          a: formatMetric(key, a),
          b: formatMetric(key, b),
          winnerSide,
        };
      });

    return {
      comparison: c,
      rows,
      faq: data.faq || [],
      relatedGuides: data.related_guides || [],
      winnerSlug: c.winner_slug,
      winnerName: c.winner_slug
        ? c.winner_slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())
        : null,
      providerAName: titleize(slugA),
      providerBName: titleize(slugB),
      providerASlug: slugA,
      providerBSlug: slugB,
    };
  }

  titleToken() {
    return this.currentModel?.comparison?.title || "Comparison";
  }
}

function titleize(slug) {
  return (slug || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
